import { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchMembership, fetchMembershipTypes, startMembershipCheckout } from '../api';

// Membership panel for a linked member.
//   - Shows current membership status (active / past_due / pending / none).
//   - If no active membership, lists purchasable tiers with a "Join" button that
//     redirects to Stripe-hosted Checkout.
//   - Reads ?checkout=success|cancel on return from Stripe and shows a banner.
//
// Payment truth is Stripe; a freshly-returned "success" may still read as
// `pending` here until the webhook promotes it, so we message that honestly.

const STATUS_LABEL = {
  active: 'Active',
  past_due: 'Past due',
  pending: 'Pending activation',
  canceled: 'Canceled',
};

function centsToUsd(cents, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format((cents || 0) / 100);
}

export default function Membership() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const accountKey = account?.homeAccountId || account?.username || null;

  const [state, setState] = useState({ loading: true, error: null, membership: null });
  const [tiers, setTiers] = useState([]);
  const [busyTierId, setBusyTierId] = useState(null);
  const [banner, setBanner] = useState(null);

  // Read the Stripe return status once on mount, then strip it from the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      setBanner({ kind: 'ok', text: 'Payment received. Your membership will activate momentarily.' });
    } else if (checkout === 'cancel') {
      setBanner({ kind: 'warn', text: 'Checkout canceled — no charge was made.' });
    }
    if (checkout) {
      params.delete('checkout');
      params.delete('session_id');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [mRes, tRes] = await Promise.all([
        fetchMembership(instance, account),
        fetchMembershipTypes(instance, account),
      ]);
      if (mRes.ok) {
        setState({ loading: false, error: null, membership: mRes.data?.membership || null });
      } else {
        const detail = (mRes.data && (mRes.data.message || mRes.data.error)) || `HTTP ${mRes.status}`;
        setState({ loading: false, error: `Could not load membership (${detail}).`, membership: null });
      }
      if (tRes.ok) setTiers(tRes.data?.membershipTypes || []);
    } catch (e) {
      setState({ loading: false, error: e.message, membership: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  useEffect(() => { load(); }, [load]);

  async function handleJoin(tierId) {
    setBusyTierId(tierId);
    setBanner(null);
    try {
      const { ok, data, status } = await startMembershipCheckout(instance, account, tierId);
      if (ok && data?.url) {
        window.location.assign(data.url); // hand off to Stripe-hosted Checkout
        return;
      }
      const detail = (data && (data.message || data.error)) || `HTTP ${status}`;
      setBanner({ kind: 'warn', text: `Could not start checkout: ${detail}` });
    } catch (e) {
      setBanner({ kind: 'warn', text: `Could not start checkout: ${e.message}` });
    } finally {
      setBusyTierId(null);
    }
  }

  const m = state.membership;
  const hasLiveMembership = m && (m.status === 'active' || m.status === 'past_due');
  const viaHousehold = !!(m && m.via_household);

  return (
    <div className="card">
      <h2>Membership</h2>

      {banner && <div className={`notice ${banner.kind === 'ok' ? '' : 'warn'}`}>{banner.text}</div>}

      {state.loading && <p className="muted">Loading membership…</p>}
      {state.error && <p className="error-text">{state.error}</p>}

      {!state.loading && m && viaHousehold && (
        <>
          <div className="notice">
            You're covered by your family membership{m.owner_name ? ` (${m.owner_name}'s household)` : ''}.
            There's nothing to pay — your membership is included.
          </div>
          <dl className="kv">
            <dt>Tier</dt><dd>{m.tier_name || '—'}</dd>
            <dt>Status</dt><dd>Covered by family membership</dd>
            <dt>Billed to</dt><dd>{m.owner_name || m.owner_email || 'Household owner'}</dd>
            {m.current_period_end && (
              <>
                <dt>Renews</dt>
                <dd>{new Date(m.current_period_end).toLocaleDateString()}</dd>
              </>
            )}
          </dl>
        </>
      )}

      {!state.loading && m && !viaHousehold && (
        <dl className="kv">
          <dt>Tier</dt><dd>{m.tier_name || '—'}</dd>
          <dt>Status</dt><dd>{STATUS_LABEL[m.status] || m.status}</dd>
          <dt>Price</dt><dd>{centsToUsd(m.price_cents, m.currency)}/{m.billing_period === 'monthly' ? 'mo' : m.billing_period}</dd>
          {m.current_period_end && (
            <>
              <dt>Renews</dt>
              <dd>{new Date(m.current_period_end).toLocaleDateString()}</dd>
            </>
          )}
        </dl>
      )}

      {!state.loading && !hasLiveMembership && !viaHousehold && (
        <div className="join">
          {m?.status === 'pending' && (
            <p className="muted">Your last checkout is still finalizing. If you just paid, give it a moment and refresh.</p>
          )}
          {!m && <p className="muted">You don't have a membership yet. Choose a tier to join:</p>}
          <ul className="tier-list">
            {tiers.filter((t) => t.stripe_price_id).map((t) => (
              <li key={t.id} className="tier">
                <div className="tier-info">
                  <strong>{t.name}</strong>
                  <span className="muted">
                    {centsToUsd(t.price_cents, t.currency)}/{t.billing_period === 'monthly' ? 'mo' : t.billing_period}
                  </span>
                  {t.description && <p className="muted">{t.description}</p>}
                </div>
                <button disabled={busyTierId === t.id} onClick={() => handleJoin(t.id)}>
                  {busyTierId === t.id ? 'Starting…' : 'Join'}
                </button>
              </li>
            ))}
            {tiers.filter((t) => t.stripe_price_id).length === 0 && (
              <li className="muted">No tiers are available for purchase yet.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
